import { useCallback } from 'react';

import { useAgendaGeneration, type AgendaGenerationRequest } from './useAgendaGeneration';

import { managerAssessmentService } from '@/api/cycle-evaluation-services/manager-assessment';

/**
 * The post-review meeting's agenda generation, for the review cycle `cycleInstanceId`, written in the reader's
 * language (the server reads it from the Accept-Language header). Only the manager's objective reaches the
 * plan: there is no private context to send.
 *
 * `generatePlanOrReport` resolves to the plan, or to null after telling the manager it failed, and throws a
 * `SensitiveObjectiveRefusal` when the server refuses the objective or a noted topic.
 */
export function useMeetingAgenda(cycleInstanceId: string) {
    const request = useCallback<AgendaGenerationRequest>(
        ({ correlationId, objectives }) =>
            managerAssessmentService.generateAgenda({ cycleInstanceId, correlationId, objectives }),
        [cycleInstanceId],
    );

    const { generateOrReport } = useAgendaGeneration(request);
    const generatePlanOrReport = useCallback(
        (objective: string) => generateOrReport(objective, ''),
        [generateOrReport],
    );

    return { generatePlanOrReport };
}
